<template>
  <transition name="modal">
    <div
      :id="modalId"
      class="overflow-y-auto modal-mask"
      :class="{
        'fullscreen': fullScreen,
      }"
      @keyup="handleKeypress"
    >
      <div class="modal-container">
        <div class="modal-header">
          <slot name="header" />
          <button
            v-if="hasClose"
            v-event-track:closeButtonClicked="{ click_source: eventTrackingSource, button_name: 'x'}"
            type="button"
            class="close"
            @click.stop="$emit('close')"
          >
            <i class="text-2xl fa-light fa-xmark" />
          </button>
        </div>

        <div class="modal-body">
          <slot />
        </div>

        <div class="modal-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import analyticsEvents from '@/utils/analyticsEvents';
import { eventTracker } from '@/utils/eventTracker';

export default {
  props: {
    hasClose: {
      type: Boolean,
      required: false,
      default: true,
    },
    // eventTrackingSource is used to specify the click_source property for event tracking
    eventTrackingSource: {
      type: String,
      required: false,
      default: '',
    },
    modalId: {
      type: String,
      required: false,
      default: 'modal-mask',
    },
    fullScreen: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    scrollingContainers: [],
    scrollY: 0,
  }),
  mounted() {
    window.addEventListener('keyup', this.handleKeypress);
    this.scrollingContainers = [
      document.querySelector('body'),
    ];
    this.updateScrollingContainers(true);
    window.addEventListener('click', this.handleClickOutside);
    // Sometimes elements are not focused programatically, setting tabIndex to -1 before
    // focusing helps grab the focus.
    // More info https://bobbyhadz.com/blog/javascript-change-active-element
    this.$el.tabIndex = '-1';
    this.$el.focus();
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.handleKeypress);
    this.updateScrollingContainers(false);
    window.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (event.target.id === this.modalId) {
        eventTracker(
          analyticsEvents.closeButtonClicked, {
            click_source: this.eventTrackingSource,
            button_name: '[clicked outside popup to close]',
          },
        );
        this.$emit('close');
      }
    },
    handleKeypress(event) {
      if (document.querySelectorAll('.modal-mask').length > 1 && !document.activeElement.closest(`#${this.modalId}`)) {
        return;
      }

      if (event.key === 'Escape') {
        eventTracker(
          analyticsEvents.closeButtonClicked, {
            click_source: this.eventTrackingSource,
            button_name: '[pressed escape]',
          },
        );
        this.$emit('close');
      }
    },
    updateScrollingContainers(removeScrollbar) {
      this.scrollingContainers.forEach((e) => {
        if (!e || !e.classList) {
          return;
        }

        if (removeScrollbar) {
          e.classList.add('overflow-hidden', 'w-full');
          this.scrollY = window.scrollY;
          document.body.style.top = `-${this.scrollY}px`;
          document.body.style.position = 'fixed';
          document.body.style.width = '100%';
        } else {
          // If more than one popups are opened, do not remove scrollbar
          if (document.querySelectorAll('.modal-mask').length > 1) {
            return;
          }
          e.classList.remove('overflow-hidden', 'w-full');
          document.body.style.position = '';
          document.body.style.top = '';
          window.scrollTo(0, this.scrollY);
        }
      });
    },
  },
};
</script>

<style scoped>
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 42px;
  z-index: 999;
}
.modal-mask.fullscreen {
  margin-top: 0;
  z-index: 1000;
}
.modal-container {
    position: relative;
    margin: 0;
    padding: 0;
    max-width: 1100px;
    background-color: #fff;
    border-radius: 0;
    transition: all 0.3s ease;
    width: 100%;
    height: 100%;
    margin-top: 6px;
}
.fullscreen .modal-container {
  margin-top: 0;
}
.modal-body {
    height: auto;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.fullscreen .modal-body {
  margin-top: 56px;
}
.close {
    position: absolute;
    cursor: pointer;
    color: #828282;
    top: 10px;
    right: 20px;
}
@screen sm {
    .close {
        right: -26px;
        top: -26px;
        color: white;
    }
    .modal-container {
        width: auto;
        height: auto;
        border-radius: 12px;
        overflow-y: initial;
        max-height: 100%;
    }
    .modal-mask {
        margin-top: 0;
        background-color: rgba(0,0,0,0.8);
    }
    .fullscreen .modal-body {
      margin-top: 0;
    }
}
@media (orientation: landscape) and (max-height: 480px) {
  .modal-mask {
      z-index: 1000;
  }
  .modal-container {
    height: 100%;
    border-radius: 0;
  }
  .close {
    top: 0;
  }
}
</style>
